// frontend/src/utils/lazySkeleton.js
//
// A page skeleton loaded on demand - the shared mechanics behind
// components/loading/LazyAdminPageSkeleton.js and LazySettingsPageSkeleton.js
// (backlog #158). Each of those is a one-line `lazySkeleton(() => import(...))`
// plus its own "why lazy" header; everything about HOW is here, once.
//
// A skeleton is a Suspense FALLBACK - what shows while a page chunk loads - so
// it is normally eager. These two are code-split because a static import from
// the route table puts them in the main chunk every visitor downloads,
// anonymous homepage visitors included. ProtectedRoute starts them as soon as
// it knows who is signed in, so normally the chunk is in long before the page
// is opened and the skeleton renders with no wait (lazyWithPreload renders a
// loaded module directly).
//
// THREE RULES:
//
//   1. While the skeleton's own chunk is still on its way (a cold boot straight
//      onto the page), its Suspense fallback is NOTHING: the page body stays
//      empty for that moment under the real navbar - never a spinner, never the
//      full-screen boot loader. (A route can keep that moment off screen by
//      not rendering its layout until the skeleton is in - the admin routes do,
//      with components/loading/AwaitPageSkeleton.js.)
//   2. A skeleton never takes the page down. A chunk that fails to load (a
//      stale chunk after a deploy, a dropped connection) is caught by a tiny
//      error boundary that renders nothing; the page's own chunk still decides
//      what happens next. This module logs nothing: a missing placeholder is
//      not an error anyone can act on.
//   3. A failure is NOT cached for the session. The factory does not swallow
//      the rejection (lazyWithPreload drops a failed import so the next
//      attempt imports again), and every failure moves the attempt counter on,
//      so the NEXT mount of the skeleton gets a fresh lazy object
//      (LazyAttemptContext) and requests the chunk again. Within one mount the
//      attempt is fixed, so a chunk that keeps failing reaches the boundary
//      once instead of being re-requested in a render loop.
//
// `.preload()` never rejects: it is fire-and-forget from an effect, and a
// failed warm-up must not surface as an unhandled rejection. It is idempotent
// while a download is in flight (the same promise comes back), and after a
// failure it tries again. `.isReady()` is true once a preload has delivered
// the module - what components/loading/AwaitPageSkeleton.js reads so a cold
// boot holds the branded loader instead of reaching rule 1's empty moment.

import React, { Component, Suspense, useState } from "react";
import lazyWithPreload, { LazyAttemptContext } from "./lazyWithPreload";

/**
 * Code-split a skeleton component.
 *
 * @param {() => Promise<{default: React.ComponentType}>} factory the skeleton's
 *        dynamic import(); must reject on a failed chunk (do not catch it)
 * @returns {React.ComponentType & {preload: () => Promise<unknown>, isReady: () => boolean}}
 *          a component that forwards every prop to the skeleton and renders
 *          nothing while the chunk loads or when it failed; `.preload()` starts
 *          (or joins) the download and resolves either way - the module, or
 *          undefined; `.isReady()` is true once a preload delivered the module
 */
export default function lazySkeleton(factory) {
  const Loaded = lazyWithPreload(factory);

  // How many times the chunk has failed to render. A mount reads it ONCE, as
  // its LazyAttemptContext value: a failure therefore hands the next mount a
  // new lazy object (rule 3) without ever changing a mounted one's.
  let failures = 0;

  /** Renders nothing once the skeleton below it has thrown (rule 2). */
  class SkeletonChunkBoundary extends Component {
    constructor(props) {
      super(props);
      this.state = { failed: false };
    }

    static getDerivedStateFromError() {
      return { failed: true };
    }

    componentDidCatch() {
      failures += 1;
    }

    render() {
      return this.state.failed ? null : this.props.children;
    }
  }

  function LazySkeleton(props) {
    const [attempt] = useState(() => failures);
    return (
      <SkeletonChunkBoundary>
        <LazyAttemptContext.Provider value={attempt}>
          <Suspense fallback={null}>
            <Loaded {...props} />
          </Suspense>
        </LazyAttemptContext.Provider>
      </SkeletonChunkBoundary>
    );
  }

  // The safe twin of the download in flight, so repeated calls during one
  // download return one promise (lazyWithPreload returns the same pending
  // promise until it settles; after a failure it starts a new one).
  let inFlight = null;
  let safe = null;
  let ready = false;
  LazySkeleton.preload = () => {
    try {
      const download = Loaded.preload();
      if (download !== inFlight) {
        inFlight = download;
        safe = Promise.resolve(download).then(
          (mod) => {
            ready = true;
            return mod;
          },
          () => undefined
        );
      }
      return safe;
    } catch (e) {
      // A factory that throws synchronously: nothing started, nothing to wait for.
      return Promise.resolve(undefined);
    }
  };

  LazySkeleton.isReady = () => ready;

  return LazySkeleton;
}
